import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { Reveal } from "@/components/site/motion";
import { BLOG } from "@/components/site/data";
import { Seo, SITE } from "@/components/site/Seo";

const Block = ({ block }) => {
  if (block.h) return <h2 className="font-display font-semibold text-2xl lg:text-3xl text-white mt-12 mb-5 tracking-tight">{block.h}</h2>;
  if (block.table)
    return (
      <figure className="my-8">
        <div className="overflow-x-auto rounded-3xl border border-[#262626]">
          <table className="w-full text-left border-collapse min-w-[560px]">
            <thead>
              <tr className="bg-[#0d0d0d] text-zinc-500 font-mono-eng text-[11px] uppercase tracking-wider">
                {block.table.head.map((h) => <th key={h} className="py-4 px-5 font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {block.table.rows.map((r, ri) => (
                <tr key={ri} className="border-t border-[#1c1c1c]">
                  <td className="py-4 px-5 font-display font-semibold text-[#fcc41b] whitespace-nowrap align-top">{r[0]}</td>
                  {r.slice(1).map((cell, ci) => (
                    <td key={ci} className="py-4 px-5 text-zinc-300 leading-relaxed">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {block.table.caption && <figcaption className="mt-3 font-mono-eng text-xs text-zinc-500">{block.table.caption}</figcaption>}
        {block.table.note && <p className="mt-2 text-zinc-500 text-sm leading-relaxed">{block.table.note}</p>}
      </figure>
    );
  if (block.ul)
    return (
      <ul className="my-6 space-y-3">
        {block.ul.map((li, i) => (
          <li key={i} className="flex items-start gap-3 text-zinc-300 text-lg leading-relaxed">
            <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-[#fcc41b] shrink-0" />
            <span>{li}</span>
          </li>
        ))}
      </ul>
    );
  return <p className="text-zinc-300 text-lg leading-relaxed my-5">{block.p}</p>;
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG.find((p) => p.slug === slug);
  const others = BLOG.filter((p) => p.slug !== slug).slice(0, 3);
  const metaImage = post && post.image.endsWith(".svg") ? SITE.defaultImage : post?.image;

  if (!post) {
    return (
      <>
        <section className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 pt-40 pb-24">
          <p className="font-mono-eng text-sm text-[#fcc41b] mb-4">404</p>
          <h1 className="font-display font-semibold text-4xl text-white mb-6">Article not found.</h1>
          <Link to="/blog" data-testid="blog-back-404" className="inline-flex items-center gap-2 text-[#fcc41b] font-display font-medium">
            <ArrowLeft size={18} /> Back to the blog
          </Link>
        </section>
        <QuoteFooter />
      </>
    );
  }

  return (
    <>
      <Seo
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt}
        image={metaImage}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: metaImage,
          articleSection: post.category,
          author: { "@type": "Person", name: post.author },
          datePublished: post.iso,
          dateModified: post.updatedIso || post.iso,
          publisher: { "@type": "Organization", name: "SolarArk Australia", logo: { "@type": "ImageObject", url: `${SITE.base}/solarark-white.png` } },
          mainEntityOfPage: `${SITE.base}/blog/${post.slug}`,
        }}
      />
      {/* Cover header */}
      <section data-testid="blog-post" className="relative overflow-hidden bg-[#010101] pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="absolute inset-0 z-0">
          <img src={post.image} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#010101] via-[#010101]/80 to-[#010101]/40" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#fcc41b] transition-colors duration-300 mb-8 text-sm font-display">
            <ArrowLeft size={16} /> All articles
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <span className="font-mono-eng text-[10px] tracking-wider uppercase text-black bg-[#fcc41b] rounded-full px-3 py-1">{post.category}</span>
            <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-white mt-6" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4rem)" }}>
              {post.title}
            </h1>
            <div className="font-mono-eng text-sm text-zinc-500 mt-6">By {post.author} · {post.date} · {post.read}</div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="relative py-4 lg:py-8 bg-[#010101]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Reveal>
            <article>
              {post.body.map((block, i) => <Block key={i} block={block} />)}
            </article>
          </Reveal>
        </div>
      </section>

      {/* More articles */}
      <section className="relative py-20 lg:py-28 bg-[#050505] border-t border-[#1c1c1c] mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="font-display font-semibold text-2xl lg:text-3xl text-white tracking-tight mb-8">More from the blog</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {others.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-more-${p.slug}`} className="group flex flex-col rounded-3xl overflow-hidden border border-[#262626] bg-[#0d0d0d] hover:border-[#fcc41b]/40 transition-colors duration-500 h-full">
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="grayscale-img w-full h-full object-cover group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="font-mono-eng text-[10px] tracking-wider uppercase text-[#fcc41b] mb-2">{p.category}</span>
                    <h3 className="font-display font-semibold text-lg text-white leading-snug mb-3">{p.title}</h3>
                    <span className="mt-auto inline-flex items-center gap-2 text-[#fcc41b] font-display font-medium text-sm">
                      Read <ArrowUpRight size={15} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <QuoteFooter blurb="Ready to move from reading to results? Request an obligation-free quote and our team will design the right solution for your project." />
    </>
  );
}
